import adminAxios from './adminAxios';
import { message } from 'antd';

const CHART_COLOR_PALETTE = [
  '#6366f1',
  '#22c55e',
  '#f97316',
  '#0ea5e9',
  '#ef4444',
  '#14b8a6',
  '#a855f7',
  '#f59e0b',
  '#10b981',
  '#8b5cf6',
];

type UsageCounterResponse = {
  total_users?: number;
  users_by_plan?: Record<string, number>;
  total_books_uploaded?: number;
  books_uploaded_by_language?: Record<string, number>;
  total_words_translated?: number;
  words_translated_by_language?: Record<string, number>;
  total_words_proofread?: number;
  words_proofread_by_language?: Record<string, number>;
  total_covers_translated?: number;
  covers_translated_by_language?: Record<string, number>;
  total_audio_hours_generated?: number;
  audio_hours_generated_by_language?: Record<string, number>;
};

type ChartDataPoint = {
  label: string;
  value: number;
};

const normalizeNumberRecord = (map?: Record<string, unknown>): Record<string, number> => {
  if (!map) {
    return {};
  }
  const result: Record<string, number> = {};
  Object.entries(map).forEach(([rawKey, rawValue]) => {
    const key = rawKey || 'unknown';
    const num = Number(rawValue);
    if (Number.isFinite(num)) {
      result[key] = num;
    }
  });
  return result;
};

const toChartDataPoints = (map?: Record<string, unknown>): ChartDataPoint[] =>
  Object.entries(map || {})
    .map(([label, value]) => ({ label: label || 'unknown', value: Number(value) || 0 }))
    .filter((entry) => Number.isFinite(entry.value) && entry.value > 0)
    .sort((a, b) => b.value - a.value);

const createBarChartImage = (
  title: string,
  data: ChartDataPoint[],
  valueFormatter?: (value: number) => string,
): string | null => {
  if (!data.length) {
    return null;
  }
  const width = 820;
  const height = 420;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return null;
  }

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.fillStyle = '#111827';
  ctx.font = 'bold 20px "Segoe UI", Arial';
  ctx.textAlign = 'center';
  ctx.fillText(title, width / 2, 32);

  const margin = { top: 60, right: 40, bottom: 90, left: 80 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;
  const maxValue = Math.max(...data.map((d) => d.value));
  if (maxValue <= 0) {
    return null;
  }

  ctx.strokeStyle = '#1f2937';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(margin.left, margin.top);
  ctx.lineTo(margin.left, margin.top + chartHeight);
  ctx.lineTo(margin.left + chartWidth, margin.top + chartHeight);
  ctx.stroke();

  const ticks = 5;
  ctx.font = '12px "Segoe UI", Arial';
  ctx.fillStyle = '#4b5563';
  ctx.textAlign = 'right';
  for (let i = 0; i <= ticks; i += 1) {
    const value = (maxValue / ticks) * i;
    const y = margin.top + chartHeight - (value / maxValue) * chartHeight;
    ctx.beginPath();
    ctx.moveTo(margin.left - 6, y);
    ctx.lineTo(margin.left, y);
    ctx.stroke();
    const label = valueFormatter ? valueFormatter(value) : value.toLocaleString();
    ctx.fillText(label, margin.left - 10, y + 4);
    ctx.strokeStyle = 'rgba(148, 163, 184, 0.25)';
    ctx.beginPath();
    ctx.moveTo(margin.left, y);
    ctx.lineTo(margin.left + chartWidth, y);
    ctx.stroke();
    ctx.strokeStyle = '#1f2937';
  }

  const barGap = chartWidth / data.length;
  const barWidth = Math.min(60, barGap * 0.6);

  data.forEach((item, index) => {
    const barHeight = (item.value / maxValue) * chartHeight;
    const x = margin.left + index * barGap + (barGap - barWidth) / 2;
    const y = margin.top + chartHeight - barHeight;

    ctx.fillStyle = CHART_COLOR_PALETTE[index % CHART_COLOR_PALETTE.length];
    ctx.fillRect(x, y, barWidth, barHeight);

    ctx.fillStyle = '#111827';
    ctx.font = '12px "Segoe UI", Arial';
    ctx.textAlign = 'center';
    const valueLabel = valueFormatter ? valueFormatter(item.value) : item.value.toLocaleString();
    ctx.fillText(valueLabel, x + barWidth / 2, Math.max(margin.top + 12, y - 8));

    ctx.save();
    ctx.translate(x + barWidth / 2, margin.top + chartHeight + 18);
    ctx.rotate(-Math.PI / 4);
    ctx.textAlign = 'right';
    ctx.fillStyle = '#374151';
    ctx.fillText(item.label, 0, 0);
    ctx.restore();
  });

  return canvas.toDataURL('image/png');
};

const createPieChartImage = (
  title: string,
  data: ChartDataPoint[],
  valueFormatter?: (value: number) => string,
): string | null => {
  if (!data.length) {
    return null;
  }
  const width = 820;
  const height = 420;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return null;
  }

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.fillStyle = '#111827';
  ctx.font = 'bold 20px "Segoe UI", Arial';
  ctx.textAlign = 'center';
  ctx.fillText(title, width / 2, 32);

  const total = data.reduce((sum, item) => sum + item.value, 0);
  if (total <= 0) {
    return null;
  }

  const pieCenterX = width / 2 - 120;
  const pieCenterY = height / 2 + 20;
  const radius = Math.min(width, height) / 2 - 120;

  let startAngle = -Math.PI / 2;
  data.forEach((item, index) => {
    const sliceAngle = (item.value / total) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(pieCenterX, pieCenterY);
    ctx.fillStyle = CHART_COLOR_PALETTE[index % CHART_COLOR_PALETTE.length];
    ctx.arc(pieCenterX, pieCenterY, radius, startAngle, startAngle + sliceAngle);
    ctx.closePath();
    ctx.fill();

    const midAngle = startAngle + sliceAngle / 2;
    const labelX = pieCenterX + Math.cos(midAngle) * (radius * 0.6);
    const labelY = pieCenterY + Math.sin(midAngle) * (radius * 0.6);

    ctx.fillStyle = '#ffffff';
    ctx.font = '12px "Segoe UI", Arial';
    ctx.textAlign = 'center';
    ctx.fillText(`${((item.value / total) * 100).toFixed(1)}%`, labelX, labelY);

    startAngle += sliceAngle;
  });

  const legendX = width - 220;
  let legendY = 80;
  ctx.font = '12px "Segoe UI", Arial';
  data.forEach((item, index) => {
    ctx.fillStyle = CHART_COLOR_PALETTE[index % CHART_COLOR_PALETTE.length];
    ctx.fillRect(legendX, legendY + index * 22, 14, 14);
    ctx.fillStyle = '#111827';
    const label = valueFormatter ? valueFormatter(item.value) : item.value.toLocaleString();
    ctx.fillText(`${item.label}: ${label}`, legendX + 20, legendY + index * 22 + 12);
  });

  return canvas.toDataURL('image/png');
};

const addSummarySheet = (workbook: any, counters: UsageCounterResponse) => {
  const summarySheet = workbook.addWorksheet('Overall');
  summarySheet.columns = [
    { header: 'Metric', key: 'metric', width: 32 },
    { header: 'Value', key: 'value', width: 22 },
  ];
  summarySheet.getRow(1).font = { bold: true };
  summarySheet.getColumn(2).alignment = { horizontal: 'right' };

  const addRow = (metric: string, value: number, numFmt = '#,##0') => {
    const row = summarySheet.addRow({ metric, value });
    row.getCell(2).numFmt = numFmt;
  };

  addRow('Total users', Number(counters.total_users || 0));
  addRow('Total books uploaded', Number(counters.total_books_uploaded || 0));
  addRow('Total words translated', Number(counters.total_words_translated || 0));
  addRow('Total words proofread', Number(counters.total_words_proofread || 0));
  addRow('Total covers translated', Number(counters.total_covers_translated || 0));
  addRow('Total audio hours generated', Number(counters.total_audio_hours_generated || 0), '0.00');
  const timestampRow = summarySheet.addRow({ metric: 'Generated at', value: new Date().toISOString() });
  timestampRow.getCell(2).numFmt = '@';
};

const addBreakdownSheet = (
  workbook: any,
  sheetName: string,
  entries: ChartDataPoint[],
  headerLabel: string,
  numFmt = '#,##0',
) => {
  const sheet = workbook.addWorksheet(sheetName);
  sheet.columns = [
    { header: 'Rank', key: 'rank', width: 8 },
    { header: 'Language / Category', key: 'label', width: 32 },
    { header: headerLabel, key: 'value', width: 20 },
  ];
  sheet.getRow(1).font = { bold: true };
  sheet.getColumn(3).alignment = { horizontal: 'right' };
  sheet.getColumn(3).numFmt = numFmt;

  if (!entries.length) {
    sheet.addRow({ rank: '', label: 'No data available', value: '' });
    return;
  }

  entries.forEach((entry, index) => {
    sheet.addRow({ rank: index + 1, label: entry.label, value: entry.value });
  });
};

const addChartsSheet = (workbook: any, datasets: { title: string; entries: ChartDataPoint[]; valueFormatter?: (v: number) => string }[]) => {
  const sheet = workbook.addWorksheet('Charts');
  for (let col = 1; col <= 16; col += 1) {
    sheet.getColumn(col).width = 18;
  }

  let chartRow = 1;
  let hasCharts = false;

  const placeImage = (dataUrl: string | null, range: string) => {
    if (!dataUrl) {
      return;
    }
    const base64 = dataUrl.replace(/^data:image\/png;base64,/, '');
    if (!base64) {
      return;
    }
    const imageId = workbook.addImage({ base64, extension: 'png' });
    sheet.addImage(imageId, range);
  };

  datasets.forEach(({ title, entries, valueFormatter }) => {
    if (!entries.length) {
      return;
    }
    hasCharts = true;
    const topEntries = entries.slice(0, Math.min(entries.length, 10));
    sheet.mergeCells(chartRow, 1, chartRow, 16);
    const titleCell = sheet.getCell(chartRow, 1);
    titleCell.value = title;
    titleCell.font = { bold: true, size: 14 };
    chartRow += 1;

    const imageStart = chartRow;
    const imageEnd = imageStart + 17;

    placeImage(createBarChartImage(`${title} - Bar`, topEntries, valueFormatter), `A${imageStart}:H${imageEnd}`);
    placeImage(createPieChartImage(`${title} - Pie`, topEntries, valueFormatter), `J${imageStart}:Q${imageEnd}`);

    chartRow = imageEnd + 3;
  });

  if (!hasCharts) {
    sheet.getCell(1, 1).value = 'No chart data available';
  }
};

const buildWorkbook = async (counters: UsageCounterResponse) => {
  const ExcelJS: any = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'AskPoeAI Admin';
  workbook.created = new Date();

  addSummarySheet(workbook, counters);

  const translated = toChartDataPoints(counters.words_translated_by_language);
  const proofread = toChartDataPoints(counters.words_proofread_by_language);
  const covers = toChartDataPoints(counters.covers_translated_by_language);
  const audio = toChartDataPoints(counters.audio_hours_generated_by_language);

  addBreakdownSheet(workbook, 'Translated', translated, 'Words');
  addBreakdownSheet(workbook, 'Proofread', proofread, 'Words');
  addBreakdownSheet(workbook, 'Covers', covers, 'Covers');
  addBreakdownSheet(workbook, 'Audio', audio, 'Hours', '0.00');

  addChartsSheet(workbook, [
    { title: 'Top translated languages', entries: translated },
    { title: 'Top proofread languages', entries: proofread },
    { title: 'Top cover languages', entries: covers },
    { title: 'Top audio languages', entries: audio, valueFormatter: (value) => value.toFixed(2) },
  ]);

  return workbook;
};

const downloadWorkbook = async (workbook: any) => {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  link.download = `usage-counters-${timestamp}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 4000);
};

export const exportUsageCountersToExcel = async (apiBase: string): Promise<void> => {
  try {
    const response = await adminAxios.get<UsageCounterResponse>(`${apiBase}/admin/stats-counters`);
    const counters = response.data || {};
    const workbook = await buildWorkbook(counters);
    await downloadWorkbook(workbook);
    message.success('Usage counters exported to Excel');
  } catch (error) {
    console.error('Failed to export usage counters', error);
    message.error('Failed to export usage counters');
    throw error;
  }
};
